import type { VersionNotificationProps } from '../components/Notification';
import type { ReadBranchType } from '@readme/api/src/mappings/branch/types';

import React, { useEffect, useMemo } from 'react';

import useClassy from '@core/hooks/useClassy';
import useDashLinkUrl from '@core/hooks/useDashLinkUrl';
import useUniqueId from '@core/hooks/useUniqueId';
import { useProjectStore, useSuperHubStore } from '@core/store';

import PlanAccessBadge from '@routes/SuperHub/components/PlanAccess/Badge';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import { notify } from '@ui/Notification';
import { RHFGroup } from '@ui/RHF';
import Truncate from '@ui/Truncate';

import BranchNameInput from '../components/BranchNameInput';
import VersionNotification from '../components/Notification';
import useBuildBranchPath from '../hooks/useBuildBranchPath';
import { useBlockingModalContext } from '../Modals';

import { useCreateBranchFormContext } from './Context';
import styles from './Form.module.scss';
import { validateSimpleBranchName } from './utils';

function ErrorNotification({ apiError, name }: { apiError: VersionNotificationProps['apiError']; name: string }) {
  return (
    <VersionNotification
      apiError={apiError}
      description={
        <>
          Failed to create <Truncate maxWidth={100}>v{name}</Truncate>. Please try again or{' '}
          <a href="mailto:support@readme.io" rel="noreferrer" target="_blank">
            reach out to support
          </a>
          .
        </>
      }
      kind="error"
      title="Problem Creating Branch"
    />
  );
}

function SuccessNotification({ name }: { name: string }) {
  const buildBranchPath = useBuildBranchPath();

  return (
    <VersionNotification
      description={
        <>
          <Truncate maxWidth={100}>v{name}</Truncate> has been created.{' '}
          <a href={buildBranchPath(name)} rel="noreferrer">
            View now
          </a>
        </>
      }
      kind="success"
      title="Branch Created"
    />
  );
}

export interface CreateBranchFormProps {
  className?: string;
  /** Method to run upon success of branch creation, but prior to redirect. */
  onSuccess?: (response: ReadBranchType) => Promise<void>;
}

export default function CreateBranchForm({ className, onSuccess }: CreateBranchFormProps) {
  const bem = useClassy(styles, 'CreateBranchForm');
  const uid = useUniqueId('CreateBranchForm');

  // branch paygate
  const planFeatures = useProjectStore(s => s.plan.features);
  const planHasBranches = planFeatures.branches;
  const dashLinkUrl = useDashLinkUrl();

  const formValue = useCreateBranchFormContext();
  const { setBlockingModalValue } = useBlockingModalContext();
  const [ephemeralBranch, versionsMap, createBranch] = useSuperHubStore(s => [
    s.branches.ephemeralBranch,
    s.branches.versionsMap,
    s.branches.createBranch,
  ]);

  /**
   * The list of existing branches for the version we're currently creating a
   * new branch from.
   */
  const branches = useMemo(() => {
    if (ephemeralBranch) {
      return versionsMap[ephemeralBranch.base]?.branches?.data || [];
    }
    return [];
  }, [ephemeralBranch, versionsMap]);

  // On initial load, immediately focus on the name field.
  useEffect(() => {
    formValue.setFocus('name');
  }, [formValue]);

  const handleSubmit = formValue.handleSubmit(async data => {
    // this should never happen since we don't render the form if
    // ephemeralBranch is null, but being extra safe for the compiler
    if (!ephemeralBranch) {
      return;
    }

    const fullBranchName = `${ephemeralBranch.base}_${data.name}`;
    // Trigger a form field error if branch name is not unique.
    if (branches.some(branch => branch.name === fullBranchName)) {
      formValue.setError('name', {
        type: 'value',
        message: 'Branch already exists.',
      });
      return;
    }

    // Trigger a form field error if branch name is not a valid git name
    // according to our simplified rules.
    const nameValidity = validateSimpleBranchName(data.name);
    if (!nameValidity.isValid) {
      formValue.setError('name', {
        type: 'value',
        message: nameValidity.error,
      });
      return;
    }

    try {
      setBlockingModalValue('Creating Branch…');
      const response = await createBranch({ name: fullBranchName });
      notify(<SuccessNotification name={response.data.name} />);

      await onSuccess?.(response);
    } catch (error) {
      notify(<ErrorNotification apiError={error} name={fullBranchName} />);
      setBlockingModalValue('');
    }
  });

  if (!ephemeralBranch) {
    return null;
  }

  return (
    <form
      autoComplete="off"
      data-color-mode="dark"
      name="create-branch-form"
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(e);
      }}
    >
      <Flex className={bem('&', className)} gap="sm" layout="col">
        {!planHasBranches && (
          <Flex className={bem('-paygate')} gap="xs" layout="col">
            <PlanAccessBadge access="locked" plan="business" />
            <p className={bem('-paygate-p')}>Switch to Business or Enterprise to create new branches.</p>
            <Button
              className={bem('-paygate-button')}
              ghost
              href={dashLinkUrl('plans')}
              kind="contrast"
              rel="noreferrer"
              size="sm"
              target="_blank"
            >
              See Plans
              <Icon aria-label="Opens in new tab" name="arrow-up-right" />
            </Button>
          </Flex>
        )}
        <RHFGroup
          className={bem('-name-form-group', !planHasBranches && '_disabled')}
          control={formValue.control}
          id={uid('name')}
          label="Branch Name"
          name="name"
          required
        >
          {({ field }) => <BranchNameInput baseVersion={ephemeralBranch.base} {...field} disabled={!planHasBranches} />}
        </RHFGroup>

        <div className={bem('-divider', !planHasBranches && '_disabled')} />

        <Button
          className={bem('-button', !planHasBranches && '_disabled')}
          disabled={!planHasBranches}
          fullWidth
          loading={formValue.formState.isSubmitting ? 'Creating Branch' : false}
          size="sm"
          type="submit"
        >
          Create Branch
        </Button>
      </Flex>
    </form>
  );
}
